import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Button, Table, Pagination, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { agentService } from '../../services/agentService';
import { toast } from 'react-hot-toast';

// Add select styling
const selectStyles = `
  .majestic-select {
    background-color: #2b2b2b !important;
    color: #ffffff !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    height: 42px !important;
    padding: 0.5rem 1rem !important;
    font-size: 0.9rem !important;
    border-radius: 8px !important;
    appearance: none !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e") !important;
    background-repeat: no-repeat !important;
    background-position: right 1rem center !important;
    background-size: 16px 12px !important;
  }
  .majestic-select:focus {
    border-color: #ffd700 !important;
    box-shadow: 0 0 0 0.25rem rgba(255, 215, 0, 0.1) !important;
    outline: none !important;
  }
`;

const UserBalanceDetails = () => {
    const { t } = useTranslation();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
    const [filters, setFilters] = useState({
        reference: '',
        userId: '',
        type: 'all'
    });

    const fetchTransactions = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const response = await agentService.getTransactions({
                ...filters,
                page,
                limit: pagination.limit
            });
            if (response.success) {
                setRecords(response.transactions);
                setPagination(response.pagination);
            }
        } catch (error) {
            console.error('Fetch transactions error:', error);
            toast.error(t('error_fetch_transactions'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.limit, t]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleReset = () => {
        setFilters({ reference: '', userId: '', type: 'all' });
    };

    const handlePageChange = (page) => {
        fetchTransactions(page);
    };

    const getAmountColor = (type, amount) => {
        if (type === 'deposit' || type === 'bonus' || (type === 'adjustment' && amount > 0)) {
            return 'var(--success-green)';
        }
        if (type === 'withdrawal' || (type === 'adjustment' && amount < 0)) {
            return 'var(--danger-red)';
        }
        return 'var(--text-primary)';
    };

    return (
        <div className="majestic-page-wrapper">
            <style>{selectStyles}</style>
            {/* Filter Bar */}
            <div className="majestic-card filter-bar mb-3">
                <Row className="g-3 align-items-center w-100">
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <span className="form-label mb-0 text-secondary small me-2" style={{ minWidth: '80px' }}>{t('bill_no')}</span>
                            <Form.Control
                                type="text"
                                name="reference"
                                placeholder={t('transaction_id')}
                                value={filters.reference}
                                onChange={handleFilterChange}
                                className="w-100"
                                style={{ minWidth: '180px' }}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <span className="form-label mb-0 text-secondary small me-2" style={{ minWidth: '80px' }}>{t('user_id')}</span>
                            <Form.Control
                                type="text"
                                name="userId"
                                placeholder={t('user_id')}
                                value={filters.userId}
                                onChange={handleFilterChange}
                                className="w-100"
                                style={{ minWidth: '150px' }}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md="auto">
                        <div className="d-flex align-items-center">
                            <span className="form-label mb-0 text-secondary small me-2" style={{ minWidth: '80px' }}>{t('type')}</span>
                            <Form.Select
                                name="type"
                                value={filters.type}
                                onChange={handleFilterChange}
                                className="w-100 majestic-select"
                                style={{ minWidth: '150px' }}
                                data-bs-theme="dark"
                            >
                                <option value="all">{t('all_types')}</option>
                                <option value="deposit">{t('recharge')}</option>
                                <option value="withdrawal">{t('withdrawal')}</option>
                                <option value="adjustment">{t('adjustment')}</option>
                                <option value="bonus">{t('bonus')}</option>
                                <option value="trade">{t('trade')}</option>
                            </Form.Select>
                        </div>
                    </Col>
                    <Col xs={12} md="auto" className="ms-auto text-end">
                        <div className="d-flex gap-2 justify-content-end">
                            <Button variant="primary" size="sm" className="px-3" onClick={() => fetchTransactions(1)}>{t('query')}</Button>
                            <Button variant="secondary" size="sm" className="px-3" onClick={handleReset}>{t('reset')}</Button>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Data Grid */}
            <div className="majestic-card p-0 majestic-table-container shadow-sm position-relative">
                {loading && (
                    <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 10 }}>
                        <Spinner animation="border" variant="gold" />
                    </div>
                )}
                <Table hover className={`border-0 mb-0 ${loading ? 'opacity-50' : ''}`}>
                    <thead>
                        <tr>
                            <th>{t('serial_number')}</th>
                            <th>{t('user')}</th>
                            <th>{t('type_of_bill')}</th>
                            <th>{t('amount')}</th>
                            <th>{t('balance_before')}</th>
                            <th>{t('balance_after')}</th>
                            <th>{t('time')}</th>
                            <th>{t('remark')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.length > 0 ? records.map((rec, idx) => (
                            <tr key={rec.id}>
                                <td style={{ color: 'var(--text-primary)' }} className="small font-monospace">{rec.reference}</td>
                                <td>
                                    <div className="d-flex flex-column">
                                        <span className="fw-bold">{rec.User?.username || 'N/A'}</span>
                                        <span className="text-secondary extra-small">ID: {rec.userId.slice(-8).toUpperCase()}</span>
                                    </div>
                                </td>
                                <td>
                                    <span style={{ color: getAmountColor(rec.type, rec.amount) }}>
                                        {t(rec.type)}
                                    </span>
                                </td>
                                <td className="fw-bold" style={{ color: getAmountColor(rec.type, rec.amount) }}>
                                    {rec.amount > 0 ? '+' : ''}{rec.amount.toFixed(2)}
                                </td>
                                <td>{rec.oldBalance.toFixed(2)}</td>
                                <td>{rec.newBalance.toFixed(2)}</td>
                                <td className="small text-secondary">{new Date(rec.createdAt).toLocaleString('en-GB', { hour12: false })}</td>
                                <td className="text-secondary small">{rec.description || '-'}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="8" className="text-center py-5 text-secondary">
                                    {loading ? t('loading') : t('no_data')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="d-flex justify-content-end mt-3">
                    <Pagination size="sm">
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={pagination.page === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} />
                        {[...Array(pagination.pages)].map((_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={i + 1 === pagination.page}
                                onClick={() => handlePageChange(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        )).slice(Math.max(0, pagination.page - 3), Math.min(pagination.pages, pagination.page + 2))}
                        <Pagination.Next onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.pages} />
                        <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={pagination.page === pagination.pages} />
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default UserBalanceDetails;

